import React, { FC, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation, RouteProps } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import { IntlProvider } from 'react-intl';
import translations from './i18n/index';
import { userRoutes } from './routes/routes';
import Header from './components/layout/header/Header';
import { getDefaultCulture } from './components/user/language/utilities/languageUtilities';
import FormEmbedPage from './pages/registrations/embed/FormEmbedPage';
import { logPageView } from './utilities/GTMModule';
import 'moment/locale/fr';
import '@mdi/font/css/materialdesignicons.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datetime/css/react-datetime.css';
import './App.css';
import './assets/scss/styles.scss';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/layout/navbar/Navbar';
import { getKreezeeSession, getOrganizationId } from './utilities/LocalStorage';
import LoginPage from './pages/login/LoginPage';
import { loginWithToken, logout } from './services/authentication/AuthenticationService';
import { setCurrent } from './slices/user';
import OrganizationSelection from './pages/organization/OrganizationSelection';
import FormConfirmPage from './pages/registrations/confirm/FormConfirmPage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ActivateAccountPage from './pages/login/ActivateAccountPage';
import AcceptInvitationPage from './pages/login/AcceptInvitationPage';
import AccountCreatedPage from './pages/login/AccountCreatedPage';
import _ from 'lodash';

function usePageViews() {
  const location = useLocation();
  React.useEffect(() => {
    logPageView();
  }, [location]);
}

const culture = getDefaultCulture();
moment.locale(culture);

export const Layout: FC = () => {
  const organization = getOrganizationId();

  return (
    <div className="app">
      <Navbar />
      <div className="app-container">
        <Header></Header>
        <Switch>
          {!organization && <Redirect to="/organization-selection" />}
          <Route exact path="/">
            <Redirect to="/registrations"></Redirect>
          </Route>
          {_.map(userRoutes, (route, idx) => {
            const Component = route.component;
            return (
              <Route exact path={route.path} key={`${idx}`}>
                <Component route={route}></Component>
              </Route>
            );
          })}
        </Switch>
      </div>
    </div>
  );
};

function PrivateRoute({ children, ...rest }: RouteProps) {
  const session = getKreezeeSession();

  return (
    <Route {...rest}>
      <React.Fragment>
        {session && session.jwt && children}
        {(!session || !session.jwt) && <Redirect to="/login"></Redirect>}
      </React.Fragment>
    </Route>
  );
}

const App: FC = () => {
  const dispatch = useDispatch();
  const organization = getOrganizationId();

  const loginFromToken = async () => {
    try {
      const user = await loginWithToken();

      if (user.data) {
        dispatch(setCurrent(user.data));
      }
    } catch {
      logout();
      dispatch(setCurrent(null));
    }
  };

  usePageViews();
  useEffect(() => {
    const session = getKreezeeSession();

    if (session && session.jwt) {
      loginFromToken();
    }
  }, []);

  return (
    <React.Fragment>
      <Sentry.ErrorBoundary>
        <IntlProvider locale={culture} messages={translations[culture]}>
          <ToastContainer />
          <Switch>
            <Route path="/registrations/embed/:formId" component={FormEmbedPage} />
            <Route path="/registrations/:formId/confirm/:submissionId" component={FormConfirmPage} />
            <Route path="/login/:invitationId?" render={() => <LoginPage isCreateAccount={false} />} />
            <Route path="/create-account/:invitationId?" render={() => <LoginPage isCreateAccount={true} />} />
            <Route path="/accept-invitation/:invitationId" component={AcceptInvitationPage} />
            <Route path="/activate-account/:token" component={ActivateAccountPage} />
            <Route path="/account-created" component={AccountCreatedPage} />
            {!organization && (
              <PrivateRoute path="/notifications">
                <NotificationsPage route={userRoutes.Notifications}></NotificationsPage>
              </PrivateRoute>
            )}
            <PrivateRoute path="/organization-selection">
              <OrganizationSelection></OrganizationSelection>
            </PrivateRoute>
            <PrivateRoute path="/">
              <Layout></Layout>
            </PrivateRoute>
          </Switch>
        </IntlProvider>
      </Sentry.ErrorBoundary>
    </React.Fragment>
  );
};

export default App;
